/**
 * idb-oplog — F7 client-side IndexedDB op-log + IR persistence.
 *
 * Pairs with the server-side oplog: every op the user
 * performs in the editor is appended here BEFORE the server-side persist
 * attempt, so reloads and offline edits don't lose the user's intent.
 *
 * Sync semantics use a `(sessionId, opSeq)` cursor. A successful
 * `/api/editor-edit/<jobId>` POST advances `lastSyncedOpSeq` /
 * `lastSyncedSessionId`; ops still in the IDB store with seq beyond
 * those values are unsynced. On boot, the SPA pulls server-side oplog
 * tail past the cursor and replays it on top of the local IR.
 *
 * IDB schema (DB_VERSION=2 in `session-store.js`):
 *
 *   - object store `oplog`: keyed by composite `[jobId, sessionId, opSeq]`,
 *     value = { jobId, sessionId, opSeq, ts, kind, ...wireFields, synced }.
 *     Index `byJob` on `jobId` for fast per-job iteration.
 *   - object store `ir`: keyed by `jobId`, value = { jobId, ir, savedAt,
 *     lastSyncedOpSeq, lastSyncedSessionId, currentSessionId }.
 *
 * Public API (functional core, IDB shell):
 *
 *   nextOpSeq(localOps, sessionId)       pure: pick next opSeq for sessionId
 *   mergeServerOplog(localOps, serverRecords)
 *                                         pure: server-first append + dedupe
 *   generateSessionId()                  ULID-ish unique id (not crypto)
 *   stripPersistenceFields(op)           pure: drop ts/sessionId/opSeq
 *   appendLocalOp(db, record)            IDB write
 *   loadLocalOps(db, jobId)              IDB read (sorted)
 *   markOpsSynced(db, jobId, cursor)     IDB update flags
 *   loadIrSnapshot(db, jobId)            IDB read
 *   storeIrSnapshot(db, jobId, snapshot) IDB write
 *   pruneSyncedOps(db, jobId, keepTail)  retention: drop synced ops past
 *                                        the keep-tail count (default 200)
 *
 * Tested by `test/idb-oplog.test.mjs` (pure logic) and
 * `test/session-store-v2.test.mjs` (schema migration).
 *
 * @module idb-oplog
 */
const PERSISTENCE_FIELDS = ["ts", "sessionId", "opSeq"];
// ─── Pure helpers (no IDB; unit-tested in idb-oplog.test.mjs) ────────────────
/**
 * Compute the next `opSeq` to use for `sessionId` in a new op appended to
 * `localOps`. Returns `max(opSeq for ops where sessionId matches) + 1`.
 *
 * Edge cases:
 *   - If `localOps` is empty or not an array, returns `0`.
 *   - If no ops in `localOps` match `sessionId`, returns `0`
 *     (conceptually: max = -1, so -1 + 1 = 0).
 *   - Ops for other sessionIds are ignored; each session has its own
 *     monotonically-increasing sequence starting from 0.
 *
 * Idempotent: does not mutate `localOps`.
 */
export function nextOpSeq(localOps, sessionId) {
    if (!Array.isArray(localOps) || localOps.length === 0)
        return 0;
    let max = -1;
    for (const op of localOps) {
        if (op?.sessionId === sessionId && typeof op.opSeq === "number" && op.opSeq > max) {
            max = op.opSeq;
        }
    }
    return max + 1;
}
/**
 * Merge the server-side oplog tail into the local oplog. Returns a `MergeResult`
 * with three fields: `merged`, `newFromServer`, `localPending`.
 *
 * Dedup key: `"${sessionId}|${opSeq}"`. Two records with the same key are
 * treated as the same logical op regardless of their other fields.
 *
 * Conflict rule (server-wins): when a server record and a local record share
 * the same dedup key, the SERVER record is used in `merged` (its fields
 * overwrite the local version). The server record is always emitted with
 * `synced=true`.
 *
 * Local-only records: ops present locally but absent from the server side
 * are kept in `merged` with their existing `synced` flag unchanged. These are
 * the "we wrote ahead of the server" records — typically in-flight or not-yet-
 * saved ops from the current session.
 *
 * Sort order: `merged` is sorted by `compareOpRecords` (ts → sessionId →
 * opSeq). The sort is stable within each tie-break level.
 *
 * Output fields:
 *   - `merged` — the deduplicated, server-wins union, sorted.
 *   - `newFromServer` — server records whose dedup key was NOT present in
 *     `localOps` (i.e., genuinely new ops not seen by this client before).
 *     These are the ops to replay on the local IR on boot.
 *   - `localPending` — local records that are NOT synced (`synced !== true`)
 *     AND not present on the server. These are ops the client wrote but has
 *     not yet successfully persisted to the server.
 *
 * Non-object elements in either array are silently skipped.
 * Non-array inputs are treated as empty arrays.
 */
export function mergeServerOplog(localOps, serverOps) {
    const local = Array.isArray(localOps) ? localOps : [];
    const server = Array.isArray(serverOps) ? serverOps : [];
    const keyOf = (r) => `${r.sessionId || ""}|${typeof r.opSeq === "number" ? r.opSeq : -1}`;
    const seen = new Map();
    // Server records win; mark them synced.
    for (const r of server) {
        if (!r || typeof r !== "object")
            continue;
        const k = keyOf(r);
        seen.set(k, { ...r, synced: true });
    }
    // Local records: if not present server-side, keep as-is (preserve
    // the local synced flag; ops that round-tripped through Save
    // already had synced=true).
    const localPending = [];
    for (const r of local) {
        if (!r || typeof r !== "object")
            continue;
        const k = keyOf(r);
        if (!seen.has(k)) {
            seen.set(k, r);
            if (!r.synced)
                localPending.push(r);
        }
    }
    const merged = Array.from(seen.values()).sort(compareOpRecords);
    // newFromServer = server records that weren't already present locally.
    const localKeys = new Set(local.map(keyOf));
    const newFromServer = server.filter((r) => r && !localKeys.has(keyOf(r)));
    return { merged, newFromServer, localPending };
}
/**
 * Comparator for sorting `OpRecord` entries. Sort key is
 * `(ts, sessionId, opSeq)` in lexicographic order (per memo §5.2):
 *
 *   1. `ts` (ISO 8601 timestamp string) — primary sort; lexicographic
 *      comparison is correct for ISO 8601 dates with consistent timezone/
 *      precision. Missing `ts` is treated as `""` (sorts before any real ts).
 *   2. `sessionId` — secondary; lexicographic. Missing sessionId treated as `""`.
 *   3. `opSeq` — tertiary; numeric ascending. Missing opSeq treated as `-1`.
 *
 * Returns negative, 0, or positive as required by `Array.prototype.sort`.
 * Suitable for use with `Array.sort(compareOpRecords)`.
 */
export function compareOpRecords(a, b) {
    const ta = (a?.ts) ?? "";
    const tb = (b?.ts) ?? "";
    if (ta !== tb)
        return ta < tb ? -1 : 1;
    const sa = (a?.sessionId) ?? "";
    const sb = (b?.sessionId) ?? "";
    if (sa !== sb)
        return sa < sb ? -1 : 1;
    const oa = (a && typeof a.opSeq === "number") ? a.opSeq : -1;
    const ob = (b && typeof b.opSeq === "number") ? b.opSeq : -1;
    return oa - ob;
}
// Strip persistence-only fields from an op envelope. Symmetric with the
// server-side strip in `web/server.py:_EDITOR_PERSISTENCE_FIELDS` — before
// forwarding to the C# CLI we keep the wire contract clean.
export function stripPersistenceFields(op) {
    if (!op || typeof op !== "object")
        return op;
    const out = {};
    for (const k of Object.keys(op)) {
        if (PERSISTENCE_FIELDS.includes(k))
            continue;
        if (k === "synced")
            continue; // local-only flag, never goes to server
        out[k] = op[k];
    }
    return out;
}
// ─── ULID-ish session ID ──────────────────────────────────────────────────────
// Format: `<48-bit ms timestamp in base32><80-bit randomness>` = 26 chars.
// Uses Crockford's base32 alphabet for parity with real ULIDs in case we
// later swap in a library implementation. NOT crypto-grade.
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const TIME_LEN = 10;
const RAND_LEN = 16;
export function generateSessionId() {
    const now = Date.now();
    let timeStr = "";
    let t = now;
    for (let i = TIME_LEN - 1; i >= 0; i--) {
        timeStr = CROCKFORD[t & 31] + timeStr;
        t = Math.floor(t / 32);
    }
    let randStr = "";
    for (let i = 0; i < RAND_LEN; i++) {
        randStr += CROCKFORD[Math.floor(Math.random() * 32)];
    }
    return timeStr + randStr;
}
// ─── IDB shell (thin wrappers; tested via session-store-v2.test.mjs) ─────────
export async function appendLocalOp(db, record) {
    return new Promise((resolve, reject) => {
        if (!record?.jobId || !record.sessionId || typeof record.opSeq !== "number") {
            reject(new TypeError("appendLocalOp: record missing jobId/sessionId/opSeq"));
            return;
        }
        const tx = db.transaction("oplog", "readwrite");
        const store = tx.objectStore("oplog");
        const req = store.put({ ...record, synced: !!record.synced });
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error("appendLocalOp put failed"));
    });
}
// Phase FB.10 (frontend-mv-architecture-260524.md §P.6) — batch-append
// variant. Writes N records inside a single IDB transaction; resolves
// on `tx.oncomplete` (the durable-on-disk signal) rather than on each
// `put.onsuccess` (in-flight). Idempotent under crash-retry because
// the composite primary key `[jobId, sessionId, opSeq]` makes a replay
// of the same batch a no-op `put` (overwrites with identical data).
export async function appendLocalOpBatch(db, batch) {
    return new Promise((resolve, reject) => {
        if (!Array.isArray(batch) || batch.length === 0) {
            resolve();
            return;
        }
        // Validate before opening the transaction — a TypeError BEFORE the
        // tx avoids leaving an empty readwrite tx open on a misuse path.
        for (const r of batch) {
            if (!r?.jobId || !r.sessionId || typeof r.opSeq !== "number") {
                reject(new TypeError("appendLocalOpBatch: record missing jobId/sessionId/opSeq"));
                return;
            }
        }
        const tx = db.transaction("oplog", "readwrite");
        const store = tx.objectStore("oplog");
        for (const rec of batch) {
            store.put({ ...rec, synced: !!rec.synced });
        }
        // Durability anchor: `tx.oncomplete` fires AFTER all puts hit disk;
        // per-put `onsuccess` only signals that the put landed in the
        // transaction's in-memory write set. The Phase FB.10 acceptance
        // ("≤200ms data loss") depends on this distinction. Per §P.6.
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("appendLocalOpBatch tx failed"));
        tx.onabort = () => reject(tx.error ?? new Error("appendLocalOpBatch tx aborted"));
    });
}
// ─── OplogBuffer — Phase FB.10 size/time-throttled batching layer ──────────
//
// At 60 fps interactive op rates, per-op IDB transactions saturate the
// browser's IDB scheduler (~1-5 ms/tx under load). OplogBuffer coalesces
// up to MAX_OPS records OR MAX_AGE_MS, whichever first, into a single
// `appendLocalOpBatch` transaction.
//
// Durability invariant: the promise returned by `enqueue(rec)` resolves
// when the batch containing `rec` has fully committed (tx.oncomplete),
// NOT when `rec` is buffered. Callers awaiting the promise observe
// durable-on-disk semantics, matching the pre-FB.10 `appendLocalOp`
// contract that `state._pendingIdbWrites` previously tracked.
//
// Flush triggers (6 mandatory, per §P.6):
//   (a) Buffer size ≥ MAX_OPS               — inside enqueue.
//   (b) Buffer age ≥ MAX_AGE_MS             — inside enqueue (setTimeout).
//   (c) `window.__edaFlushPendingIdbWrites` — wired in editor.ts.
//   (d) `beforeunload` / `pagehide`         — wired in editor.ts.
//   (e) `visibilitychange` → hidden         — wired in editor.ts.
//   (f) Save button click                   — wired in editor-modals-save.ts.
//
// Worst-case data loss: ≤200 ms of unbatched ops if a hard crash bypasses
// hooks (d) and (e). Acceptable per §P.6.
export class OplogBuffer {
    _db;
    _pending = [];
    _timer = null;
    // Deferred that resolves when the next flush completes. Constructed
    // lazily on first enqueue; reset to a new deferred after each flush
    // begins. CRITICAL: never assign Promise.resolve() here — the
    // schematic in §P.6 has a subtle race if init uses an already-
    // resolved promise (callers would observe durability before the
    // IDB write actually completes). Decision-record: this comment.
    _flushDeferred = null;
    static MAX_OPS = 8;
    static MAX_AGE_MS = 200;
    constructor(db) {
        this._db = db;
    }
    _ensureDeferred() {
        if (this._flushDeferred !== null)
            return this._flushDeferred;
        let resolveFn = () => { };
        let rejectFn = () => { };
        const promise = new Promise((resolve, reject) => {
            resolveFn = resolve;
            rejectFn = reject;
        });
        const d = { promise, resolve: resolveFn, reject: rejectFn };
        this._flushDeferred = d;
        return d;
    }
    /**
     * Append a record to the buffer. The returned promise resolves when
     * the batch containing this record is durable on disk.
     *
     * - If buffer hits MAX_OPS: flush immediately, returned promise tracks that flush.
     * - Otherwise: arm a MAX_AGE_MS timer (idempotent — only the first armer wins).
     * - Returned promise = the in-flight or next-flush deferred.
     */
    async enqueue(rec) {
        this._pending.push(rec);
        const deferred = this._ensureDeferred();
        if (this._pending.length >= OplogBuffer.MAX_OPS) {
            // Fire and forget — `deferred.promise` already references the
            // settle path. We must NOT return flush() directly because
            // flush() returns the underlying appendLocalOpBatch promise,
            // which is settled by the same code that settles the deferred —
            // callers awaiting `enqueue()` await `deferred.promise`.
            void this.flush();
        }
        else
            this._timer ??= setTimeout(() => { void this.flush(); }, OplogBuffer.MAX_AGE_MS);
        return deferred.promise;
    }
    /**
     * Flush the buffer NOW. Resolves when the batch is durable on disk
     * (tx.oncomplete). Safe to call when buffer is empty — resolves
     * immediately. Safe to call concurrently — if a flush is already
     * in-flight, the next call after pending is non-empty will start a
     * NEW deferred for the next batch (the in-flight one continues to
     * its tx.oncomplete settlement).
     */
    async flush() {
        if (this._timer !== null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        if (this._pending.length === 0) {
            // Empty buffer: resolve any armed (but never-populated) deferred.
            // This branch is reached when explicit flush() is called against
            // an empty buffer — common during beforeunload when there's
            // nothing pending.
            if (this._flushDeferred !== null) {
                const d = this._flushDeferred;
                this._flushDeferred = null;
                d.resolve();
                return d.promise;
            }
            return;
        }
        const batch = this._pending;
        this._pending = [];
        const deferred = this._flushDeferred ?? this._ensureDeferred();
        this._flushDeferred = null;
        const tx = appendLocalOpBatch(this._db, batch);
        // WHY then/catch: we want the deferred to settle when the IDB tx
        // completes; the deferred is what callers of enqueue() awaited.
        /* eslint-disable promise/prefer-await-to-then -- bridging IDB tx → deferred settle */
        tx.then(() => deferred.resolve(), (err) => deferred.reject(err));
        /* eslint-enable promise/prefer-await-to-then */
        return tx;
    }
    /** Test-only: peek at buffer contents without flushing. */
    get pendingCount() { return this._pending.length; }
}
// ─── Module-singleton accessor + flush-hook installer ────────────────────────
//
// Why a module-singleton: per-call construction of an `OplogBuffer` would
// defeat the buffering — the whole point is that successive enqueue() calls
// land in the same batch. `state.editorDb` opens lazily; the accessor
// re-creates the buffer if the db handle changes (e.g., re-open after IDB
// schema-block resolves).
let _bufferSingleton = null;
let _bufferDb = null;
export function getOplogBuffer(db) {
    if (_bufferSingleton === null || _bufferDb !== db) {
        _bufferSingleton = new OplogBuffer(db);
        _bufferDb = db;
    }
    return _bufferSingleton;
}
/** Test-only: forget the singleton so the next getOplogBuffer creates fresh. */
export function _resetOplogBufferForTests() {
    _bufferSingleton = null;
    _bufferDb = null;
}
/**
 * Convenience: enqueue if a buffer can be obtained, else fall back to
 * the per-op `appendLocalOp` path. Callers in editor-card-pane.ts use
 * this to avoid having to thread the buffer reference themselves.
 */
export async function enqueueLocalOp(db, record) {
    return getOplogBuffer(db).enqueue(record);
}
/**
 * Wire the lifecycle flush triggers (d) + (e) onto a host (window in
 * production, FakeWindow in tests). The Save-button trigger (f) is
 * wired separately in `editor-modals-save.ts` — it's tied to a specific
 * DOM element, not the global window.
 *
 * The returned teardown removes the listeners; production never calls
 * it (the page is going away anyway), but tests use it to reset state.
 */
export function installFlushHooks(host, getBuffer) {
    const fireFlush = () => {
        const buf = getBuffer();
        if (!buf)
            return;
        // Fire-and-forget — beforeunload/pagehide listeners cannot reliably
        // await async work. The buffer.flush() call kicks off the IDB tx
        // synchronously (transaction + put calls are sync); the actual
        // disk commit happens in the browser's IDB scheduler, which
        // chrome/firefox/safari all attempt to complete before tab teardown
        // when the tx was opened pre-unload. This is best-effort by
        // necessity — the no-data-loss guarantee comes from the size/age
        // triggers, not from these lifecycle hooks.
        void buf.flush();
    };
    const visibilityHandler = () => {
        const doc = host.document;
        if (doc?.visibilityState === "hidden")
            fireFlush();
    };
    host.addEventListener("beforeunload", fireFlush);
    host.addEventListener("pagehide", fireFlush);
    if (host.document) {
        host.document.addEventListener("visibilitychange", visibilityHandler); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }
    return () => {
        if (host.removeEventListener) {
            host.removeEventListener("beforeunload", fireFlush);
            host.removeEventListener("pagehide", fireFlush);
        }
        if (host.document?.removeEventListener) {
            host.document.removeEventListener("visibilitychange", visibilityHandler); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
    };
}
export async function loadLocalOps(db, jobId) {
    return new Promise((resolve, reject) => {
        if (!jobId) {
            resolve([]);
            return;
        }
        const tx = db.transaction("oplog", "readonly");
        const store = tx.objectStore("oplog");
        const idx = store.index("byJob");
        const req = idx.getAll(jobId);
        req.onsuccess = () => {
            const all = Array.isArray(req.result) ? req.result : [];
            all.sort(compareOpRecords);
            resolve(all);
        };
        req.onerror = () => reject(req.error ?? new Error("loadLocalOps read failed"));
    });
}
export async function markOpsSynced(db, jobId, cursor) {
    return new Promise((resolve, reject) => {
        if (!jobId || !cursor?.sessionId || typeof cursor.opSeq !== "number") {
            resolve(0);
            return;
        }
        const tx = db.transaction("oplog", "readwrite");
        const store = tx.objectStore("oplog");
        const idx = store.index("byJob");
        const req = idx.openCursor(jobId);
        let updated = 0;
        // Abort-on-first-error handler: calls tx.abort() so all updates applied
        // so far are rolled back, then rejects the outer Promise. Explicit abort
        // is the IDB-canonical all-or-nothing pattern; browser auto-abort-on-
        // propagation is implementation-dependent (Safari divergences; a future
        // preventDefault() call would disable it silently).
        // See docs/design/idb-oplog-prunesyncedops-consistency.md §3 Option (a).
        const abortAndReject = (err) => {
            try {
                tx.abort();
            }
            catch (_) { /* tx may already be inactive */ }
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- IDB error events produce DOMException (extends Error) or event objects; re-wrapping would hide the original cause
            reject(err ?? new Error("markOpsSynced failed"));
        };
        req.onsuccess = (ev) => {
            const c = ev.target.result;
            if (!c) {
                resolve(updated);
                return;
            }
            const rec = c.value;
            if (rec && !rec.synced && shouldMarkSynced(rec, cursor)) {
                const upd = c.update({ ...rec, synced: true });
                // Advance only from the update's onsuccess, not immediately.
                // This ensures a failed update fires onerror + abort before
                // the cursor advances to the next record — preventing the
                // "continue races ahead of update fault" problem in shims
                // and matching real-IDB's transaction-level abort semantics.
                upd.onsuccess = () => { updated += 1; c.continue(); };
                upd.onerror = () => abortAndReject(upd.error ?? new Error("markOpsSynced cursor failed"));
            }
            else {
                c.continue();
            }
        };
        req.onerror = () => abortAndReject(req.error ?? new Error("markOpsSynced cursor failed"));
    });
}
export function shouldMarkSynced(rec, cursor) {
    if (!rec || !cursor)
        return false;
    if (rec.sessionId !== cursor.sessionId)
        return false;
    return typeof rec.opSeq === "number" && rec.opSeq <= cursor.opSeq;
}
export async function loadIrSnapshot(db, jobId) {
    return new Promise((resolve, reject) => {
        if (!jobId) {
            resolve(null);
            return;
        }
        const tx = db.transaction("ir", "readonly");
        const store = tx.objectStore("ir");
        const req = store.get(jobId);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error ?? new Error("loadIrSnapshot read failed"));
    });
}
export async function storeIrSnapshot(db, jobId, snapshot) {
    return new Promise((resolve, reject) => {
        if (!jobId) {
            reject(new TypeError("storeIrSnapshot: missing jobId"));
            return;
        }
        const tx = db.transaction("ir", "readwrite");
        const store = tx.objectStore("ir");
        const req = store.put({ jobId, ...snapshot });
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error("storeIrSnapshot put failed"));
    });
}
export async function pruneSyncedOps(db, jobId, keepTail = 200) {
    return new Promise((resolve, reject) => {
        if (!jobId) {
            resolve(0);
            return;
        }
        const tx = db.transaction("oplog", "readwrite");
        const store = tx.objectStore("oplog");
        const idx = store.index("byJob");
        const req = idx.getAll(jobId);
        req.onsuccess = () => {
            const all = Array.isArray(req.result) ? req.result : [];
            const synced = all.filter((r) => r?.synced).sort(compareOpRecords);
            // Keep the LAST `keepTail` synced records; prune older ones.
            const toPrune = synced.length > keepTail
                ? synced.slice(0, synced.length - keepTail)
                : [];
            if (toPrune.length === 0) {
                resolve(0);
                return;
            }
            const delTx = db.transaction("oplog", "readwrite");
            const delStore = delTx.objectStore("oplog");
            let pending = toPrune.length;
            for (const r of toPrune) {
                const dr = delStore.delete([r.jobId, r.sessionId, r.opSeq]);
                dr.onsuccess = () => { pending -= 1; if (pending === 0)
                    resolve(toPrune.length); };
                dr.onerror = (event) => {
                    // Abort-on-first-error: roll back every delete already applied
                    // in delTx so the store is left unchanged on rejection.
                    // WHY: explicit abort is the IDB-canonical all-or-nothing
                    // pattern; auto-abort-on-propagation is browser-dependent and
                    // a future event.preventDefault() call would disable it silently.
                    // See docs/design/idb-oplog-prunesyncedops-consistency.md §3 Option (a).
                    try {
                        delTx.abort();
                    }
                    catch (_) { /* tx may already be inactive */ }
                    reject(new Error(`pruneSyncedOps delete failed: ${event.target.error}`));
                };
            }
        };
        req.onerror = () => reject(req.error ?? new Error("pruneSyncedOps read failed"));
    });
}
// Parse an NDJSON / JSONL response body into an array of records. The
// server's `/api/editor-oplog/{jobId}` returns `application/x-ndjson`.
// Empty bodies + truncated trailing lines are tolerated (a JSONL writer
// mid-flush truncation is detectable; per memo §1.2 we discard it).
export function parseJsonlBody(text) {
    if (!text || typeof text !== "string")
        return [];
    const out = [];
    for (const line of text.split("\n")) {
        const trimmed = line.trim();
        if (!trimmed)
            continue;
        try {
            const obj = JSON.parse(trimmed);
            if (obj && typeof obj === "object")
                out.push(obj);
        }
        catch {
            // Skip malformed lines (truncated final line on crash);
            // memo §1.2 explicitly tolerates this.
        }
    }
    return out;
}
// Exported for tests.
export const __internals = { PERSISTENCE_FIELDS, CROCKFORD, TIME_LEN, RAND_LEN };
